/**
 * Phase 8 operations visibility: one provider/integration board and notification delivery operations.
 * The board shows what each integration REALLY is — شبیه‌ساز (mock), غیرفعال, پیکربندی‌نشده (owner
 * input), سالم / ضعیف / خطا (live, from breakers or the poller's observations) or «بررسی نشده» (live but
 * not probed). A simulator is never called healthy. No endpoint, credential or chat id is ever shown.
 */
import {
  AppContainer,
  Badge,
  Card,
  DataTable,
  FilterChips,
  KpiTile,
  Ltr,
  PageHeader,
  SectionHeader,
} from '@hopex/ui';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { dt } from '../api.ts';
import { Remote, useRemote } from '../lib/remote.tsx';
import { CrisisCard } from './CrisisCard.tsx';
import { SystemHealthCard } from './SystemHealth.tsx';

type State = 'MOCK' | 'INACTIVE' | 'UNCONFIGURED' | 'HEALTHY' | 'DEGRADED' | 'ERROR' | 'NOT_PROBED';
interface Row {
  key: string;
  group: 'IDENTITY' | 'MESSAGING' | 'CRYPTO' | 'MARKET' | 'STORAGE' | 'SUPPORT';
  name: string;
  mode: 'MOCK' | 'INACTIVE' | 'UNCONFIGURED' | 'LIVE';
  state: State;
  note: string | null;
  lastSeenAt: string | null;
}

export const PROVIDER_STATE: Record<
  State,
  { label: string; tone: 'neutral' | 'accent' | 'positive' | 'negative' }
> = {
  MOCK: { label: 'شبیه‌ساز', tone: 'accent' },
  INACTIVE: { label: 'غیرفعال', tone: 'neutral' },
  UNCONFIGURED: { label: 'پیکربندی‌نشده', tone: 'neutral' },
  HEALTHY: { label: 'سالم', tone: 'positive' },
  DEGRADED: { label: 'ضعیف / کهنه', tone: 'accent' },
  ERROR: { label: 'خطا', tone: 'negative' },
  NOT_PROBED: { label: 'پیکربندی‌شده · بررسی نشده', tone: 'neutral' },
};
const GROUP: Record<Row['group'], string> = {
  IDENTITY: 'هویت و بانک',
  MESSAGING: 'پیام‌رسانی',
  CRYPTO: 'رمزارز (ریسک، کارمزد، قیمت مرجع)',
  MARKET: 'منابع نرخ و بازار',
  STORAGE: 'ذخیره‌سازی مدارک',
  SUPPORT: 'پشتیبانی',
};
const KEY_FA: Record<string, string> = {
  sms: 'پیامک',
  phoneOwnership: 'تطبیق مالکیت موبایل',
  civilIdentity: 'استعلام هویت',
  bankIdentity: 'مالکیت کارت و شبا',
  screening: 'غربالگری ریسک (OPEN-22)',
  feeReference: 'مرجع کارمزد شبکه',
  priceReference: 'قیمت دلاری مرجع',
  evidenceStorage: 'مخزن خصوصی مدارک',
  telegram: 'ربات تلگرام',
  assistant: 'دستیار هوشمند پشتیبانی',
};
const NOTE: Record<string, string> = {
  AWAITING_CONTRACT: 'منتظر قرارداد و اطلاعات اتصال (مالک)',
  OPEN_22: 'انتخاب ارائه‌دهنده با مالک (OPEN-22)',
  OPEN_23: 'ربات رسمی هنوز معرفی نشده (OPEN-23)',
  OPEN_21: 'ارائه‌دهنده‌ی هوش مصنوعی تعیین نشده (OPEN-21)',
  OPEN_05_39: 'مخزن، کلید و سیاست نگه‌داری با مالک (OPEN-05/39)',
  CONFIGURED_NOT_PROBED: 'پیکربندی شده؛ سلامت آن از اینجا آزموده نمی‌شود',
  FLAG_OFF: 'پرچم قابلیت خاموش است',
  SOURCE_DISABLED: 'منبع غیرفعال است',
  L3_FIAT_SOURCE: 'منبع ریالی تأییدشده‌ی مالک لازم است (L3)',
  NO_OFFICIAL_API: 'API رسمی قابل تأیید ندارد',
  NO_ADAPTER: 'آداپتوری برای این ارائه‌دهنده وجود ندارد',
  NO_OBSERVATIONS: 'هنوز مشاهده‌ای ثبت نشده (پایشگر نرخ غیرفعال است)',
  STALE: 'آخرین مشاهده کهنه است',
};

export function ProviderBoardPage() {
  const r = useRemote<{ asOf: string; rows: Row[] }>('/providers/status');
  return (
    <AppContainer>
      <PageHeader
        title="سلامت سرویس‌ها و یکپارچه‌سازی‌ها"
        description="هر یکپارچه‌سازی با ماهیت واقعی‌اش: شبیه‌ساز، غیرفعال، پیکربندی‌نشده یا ارائه‌دهنده‌ی واقعی. شبیه‌ساز هرگز «سالم» نامیده نمی‌شود."
      />
      <div className="mb-4 flex flex-col gap-4">
        <CrisisCard />
        <SystemHealthCard />
      </div>
      <Remote r={r}>
        {(d) => (
          <div className="grid gap-4 xl:grid-cols-2" data-provider-board>
            {(Object.keys(GROUP) as Row['group'][]).map((g) => {
              const rows = d.rows.filter((x) => x.group === g);
              if (rows.length === 0) return null;
              return (
                <Card key={g} className="p-4 sm:p-5" aria-labelledby={`pg-${g}`}>
                  <SectionHeader id={`pg-${g}`} title={GROUP[g]} />
                  <ul className="m-0 list-none divide-y divide-line p-0">
                    {rows.map((x) => {
                      const s = PROVIDER_STATE[x.state];
                      return (
                        <li
                          key={`${x.key}-${x.name}`}
                          className="flex items-start justify-between gap-3 py-2.5 text-[13px]"
                          data-provider={x.key}
                          data-provider-state={x.state}
                        >
                          <span className="min-w-0">
                            <span className="block font-semibold">
                              {KEY_FA[x.key] ??
                                (x.key.startsWith('rate:')
                                  ? x.name.replace(/\s*\([^)]*\)$/, '')
                                  : x.name)}
                            </span>
                            <Ltr className="block text-[11.5px] text-fg-subtle">
                              {x.key.startsWith('rate:') ? x.key.slice(5) : x.name}
                            </Ltr>
                            {x.note && (
                              <span className="block text-[11.5px] text-fg-muted">
                                {NOTE[x.note] ?? x.note}
                              </span>
                            )}
                          </span>
                          <span className="flex shrink-0 flex-col items-end gap-1">
                            <Badge tone={s.tone}>{s.label}</Badge>
                            {x.lastSeenAt && (
                              <span className="text-[11px] text-fg-subtle">{dt(x.lastSeenAt)}</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              );
            })}
            <p className="m-0 text-[12px] text-fg-subtle xl:col-span-2">
              به‌روز در {dt(d.asOf)}. سلامت منابع نرخ از آخرین مشاهده‌ی پایشگر خوانده می‌شود؛ جزئیات
              در{' '}
              <Link to="/rates" className="font-semibold text-accent-text hover:underline">
                نرخ‌ها و سلامت منابع
              </Link>
              .
            </p>
          </div>
        )}
      </Remote>
    </AppContainer>
  );
}

interface Delivery {
  id: string;
  channel: string;
  state: string;
  attempts: number;
  lastError: string | null;
  template: string;
  category: string;
  userId: string;
  userPublicId: string;
  eventAt: string;
  updatedAt: string;
}
const DSTATE: Record<
  string,
  { label: string; tone: 'neutral' | 'accent' | 'positive' | 'negative' }
> = {
  QUEUED: { label: 'در صف', tone: 'neutral' },
  SENT: { label: 'ارسال شد', tone: 'positive' },
  DELIVERED: { label: 'تحویل شد', tone: 'positive' },
  FAILED: { label: 'ناموفق', tone: 'negative' },
  SUPPRESSED_BY_PREFERENCE: { label: 'خاموش به انتخاب کاربر', tone: 'neutral' },
  SUPPRESSED_NOT_LINKED: { label: 'کانال متصل نیست', tone: 'neutral' },
};
const CAT: Record<string, string> = {
  ACCOUNT: 'حساب',
  SECURITY: 'امنیت',
  KYC: 'احراز هویت',
  DEPOSIT: 'واریز',
  WITHDRAWAL: 'برداشت',
  CONVERT: 'تبدیل',
  SUPPORT: 'پشتیبانی',
  SYSTEM: 'اطلاعیه',
};
const FILTERS = [
  { id: '', label: 'همه' },
  { id: 'FAILED', label: 'ناموفق' },
  { id: 'SENT', label: 'ارسال‌شده' },
  { id: 'SUPPRESSED_BY_PREFERENCE', label: 'خاموش به انتخاب کاربر' },
];

export function NotificationOpsPage() {
  const [state, setState] = useState('');
  const r = useRemote<{
    telegramConfigured: boolean;
    telegramMock: boolean;
    inAppEvents24h: number;
    totals: Record<string, number>;
    items: Delivery[];
  }>(`/notifications/deliveries${state ? `?state=${state}` : ''}`);
  return (
    <AppContainer>
      <PageHeader
        title="عملیات اعلان‌ها"
        description="سوابق تحویل در کانال‌های بیرونی. متن و جزئیات اعلان‌ها اینجا نمایش داده نمی‌شود؛ شکست تحویل هیچ عملیات مالی را برنمی‌گرداند."
      />
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-4" data-notification-ops>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <KpiTile
                label="رویدادهای درون‌برنامه (۲۴ ساعت)"
                value={<bdi className="hx-num">{d.inAppEvents24h.toLocaleString('en-US')}</bdi>}
              />
              <KpiTile
                label="ارسال‌شده"
                value={<bdi className="hx-num">{(d.totals.SENT ?? 0).toLocaleString('en-US')}</bdi>}
              />
              <KpiTile
                label="ناموفق"
                value={
                  <bdi className="hx-num">{(d.totals.FAILED ?? 0).toLocaleString('en-US')}</bdi>
                }
                tone={d.totals.FAILED ? 'negative' : 'neutral'}
              />
              <KpiTile
                label="خاموش به انتخاب کاربر"
                value={
                  <bdi className="hx-num">
                    {(d.totals.SUPPRESSED_BY_PREFERENCE ?? 0).toLocaleString('en-US')}
                  </bdi>
                }
              />
            </div>
            <Card
              className="flex flex-wrap items-center justify-between gap-2 p-4 text-[13px]"
              data-telegram-state
            >
              <span className="font-semibold">کانال تلگرام</span>
              {d.telegramMock ? (
                <Badge tone="accent">شبیه‌ساز (محیط آزمایشی)</Badge>
              ) : d.telegramConfigured ? (
                <Badge tone="positive">پیکربندی‌شده</Badge>
              ) : (
                <Badge>پیکربندی‌نشده — ربات رسمی با مالک (OPEN-23)</Badge>
              )}
              <span className="w-full text-[12px] text-fg-muted">
                پیامک فعلاً فقط برای کدهای یکبارمصرف و رویدادهای امنیتی استفاده می‌شود؛ ارسال پیامکی
                سایر دسته‌ها با تصمیم مالک است (OPEN-17).
              </span>
            </Card>
            <FilterChips label="وضعیت تحویل" options={FILTERS} value={state} onChange={setState} />
            <Card className="p-3 sm:p-4">
              <DataTable<Delivery>
                label="سوابق تحویل"
                rows={d.items}
                density="compact"
                columns={[
                  {
                    id: 'cat',
                    header: 'دسته',
                    isRowHeader: true,
                    cell: (x) => CAT[x.category] ?? x.category,
                  },
                  {
                    id: 'tpl',
                    header: 'رویداد',
                    cell: (x) => <Ltr className="text-[11.5px]">{x.template}</Ltr>,
                  },
                  { id: 'ch', header: 'کانال', cell: (x) => x.channel },
                  {
                    id: 'state',
                    header: 'وضعیت',
                    cell: (x) => {
                      const s = DSTATE[x.state] ?? { label: x.state, tone: 'neutral' as const };
                      return <Badge tone={s.tone}>{s.label}</Badge>;
                    },
                  },
                  { id: 'att', header: 'تلاش', numeric: true, cell: (x) => x.attempts },
                  {
                    id: 'user',
                    header: 'کاربر',
                    cell: (x) => (
                      <Link to="/users/$id" params={{ id: x.userId }} className="hover:underline">
                        <Ltr>{x.userPublicId}</Ltr>
                      </Link>
                    ),
                  },
                  { id: 'at', header: 'آخرین تغییر', cell: (x) => dt(x.updatedAt) },
                ]}
                empty={
                  <p className="m-0 text-center text-[13px] text-fg-muted">
                    سابقه‌ی تحویلی در این نما نیست.
                  </p>
                }
              />
            </Card>
          </div>
        )}
      </Remote>
    </AppContainer>
  );
}
